import { Link } from "react-router-dom";
import SiteLayout from "@/components/site/SiteLayout";
import PageHero from "@/components/site/PageHero";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import fittingKitImg from "@/assets/fitting-kit.jpg";
import { useI18n } from "@/lib/i18n";

export default function JakToDziala() {
  const { t, path } = useI18n();

  return (
    <SiteLayout>
      <PageHero eyebrow={t.how.eyebrow} title={t.how.title} description={t.how.description} />

      <section className="py-16 lg:py-20 border-b border-border">
        <div className="mx-auto max-w-container px-6 lg:px-10">
          <div className="max-w-2xl mb-10 reveal">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">{t.how.timelineEyebrow}</div>
            <h2 className="font-serif text-[36px] lg:text-[48px] leading-[1.1] tracking-tight">{t.how.timelineTitle}</h2>
          </div>

          <div className="relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border hidden md:block" />
            <ol className="space-y-8">
              {t.how.timeline.map(([day, title, body]) => (
                <li key={day} className="grid md:grid-cols-[180px_1fr] gap-6 md:gap-12 reveal">
                  <div className="flex md:items-start gap-4">
                    <span className="hidden md:block h-3.5 w-3.5 rounded-full bg-primary mt-1.5 relative z-10" />
                    <div className="text-sm uppercase tracking-[0.16em] text-muted-foreground pt-0.5">{day}</div>
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl mb-2">{title}</h3>
                    <p className="text-muted-foreground leading-relaxed max-w-xl">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 border-b border-border">
        <div className="mx-auto max-w-container px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="reveal order-2 lg:order-1">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">Fitting Kit</div>
              <h2 className="font-serif text-[36px] lg:text-[48px] leading-[1.1] tracking-tight">{t.how.fittingTitle}</h2>
              <p className="mt-5 text-muted-foreground leading-relaxed text-lg">{t.how.fittingBody}</p>
              <ul className="mt-8 space-y-5">
                {t.how.fittingItems.map(([title, body], i) => (
                  <li key={title} className="flex gap-4">
                    <span className="font-serif text-2xl text-primary leading-none pt-1">0{i + 1}</span>
                    <div>
                      <h3 className="font-medium mb-1">{title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="reveal order-1 lg:order-2">
              <div className="overflow-hidden rounded-lg border border-border bg-secondary">
                <img src={fittingKitImg} alt={t.how.fittingAlt} loading="lazy" width={1280} height={960} className="w-full h-auto object-cover" />
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.16em] text-muted-foreground">{t.how.fittingNote}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-secondary border-b border-border">
        <div className="mx-auto max-w-container px-6 lg:px-10 grid lg:grid-cols-[1fr_2fr] gap-12">
          <div className="reveal">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">FAQ</div>
            <h2 className="font-serif text-[36px] lg:text-[44px] leading-[1.1] tracking-tight">{t.how.faqTitle}</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {t.how.faqPrompt}{" "}
              <Link to={path("contact")} className="text-foreground underline underline-offset-4 decoration-border hover:decoration-foreground">
                {t.how.faqLink}
              </Link>
              .
            </p>
          </div>
          <div className="reveal">
            <Accordion type="single" collapsible className="w-full">
              {t.how.faqs.map(([q, a], i) => (
                <AccordionItem key={q} value={`item-${i}`} className="border-border">
                  <AccordionTrigger className="text-left font-serif text-lg lg:text-xl hover:no-underline py-6">{q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed text-base pb-6">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-container px-6 lg:px-10 reveal">
          <h2 className="font-serif text-[36px] lg:text-[52px] leading-[1.05] tracking-tight max-w-5xl">{t.how.ctaTitle}</h2>
          <Link to={path("contact")} className="mt-7 inline-flex items-center rounded-md bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
            {t.nav.demo}
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
